import { useNavigate } from "react-router-dom";
import { StatusChip } from "@/components/StatusChip";
import { statusMeta, formatDate, OUTCOME_META } from "@/lib/format";
import { AlertTriangle, CalendarClock, ChevronRight, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";

export function CaseCard({ c, showManager = false }) {
  const navigate = useNavigate();
  const sm = statusMeta(c.status);
  const outcome = c.outcome ? OUTCOME_META[c.outcome] : null;

  return (
    <button
      onClick={() => navigate(`/cases/${c.id}`)}
      data-testid={`case-card-${c.id}`}
      className={cn(
        "group w-full text-left rounded-2xl bg-patria-surface border p-4 sm:p-5 transition-all hover:-translate-y-0.5",
        c.at_risk ? "border-patria-terracotta/40" : "border-patria-muted/15 hover:border-patria-green/30"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-heading font-semibold text-patria-ink truncate">{c.employee_name}</p>
          <p className="text-xs text-patria-muted truncate">{c.employee_title}</p>
        </div>
        <ChevronRight className="h-4 w-4 text-patria-muted group-hover:text-patria-green transition-colors shrink-0" />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <StatusChip label={sm.label} tone={sm.tone} testId={`case-status-${c.id}`} />
        {outcome && <StatusChip label={outcome.label} tone={outcome.tone} />}
        {c.duration && (
          <span className="text-xs text-patria-muted rounded-full bg-patria-sage/60 px-2.5 py-1">{c.duration}-day</span>
        )}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-patria-muted">
        <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{c.campus}</span>
        {showManager && c.manager_name && <span>Mgr: {c.manager_name}</span>}
        {c.next_checkin && (
          <span className="inline-flex items-center gap-1.5"><CalendarClock className="h-3.5 w-3.5" />Next check-in {formatDate(c.next_checkin)}</span>
        )}
      </div>

      {(c.overdue_count > 0 || c.past_review) && (
        <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-patria-terracottaDeep bg-patria-terracotta/10 rounded-lg px-2.5 py-1.5">
          <AlertTriangle className="h-3.5 w-3.5" />
          {c.overdue_count > 0 && `${c.overdue_count} overdue check-in${c.overdue_count > 1 ? "s" : ""}`}
          {c.overdue_count > 0 && c.past_review && " · "}
          {c.past_review && "Past final-review date"}
        </div>
      )}
    </button>
  );
}

export function StatCard({ label, value, tone = "neutral", icon: Icon, testId }) {
  const toneClasses = {
    green: "text-patria-green",
    amber: "text-patria-terracotta",
    red: "text-patria-terracottaDeep",
    neutral: "text-patria-ink",
  };
  return (
    <div className="rounded-2xl bg-patria-surface border border-patria-muted/15 p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-patria-muted uppercase tracking-wide">{label}</p>
        {Icon && <Icon className={cn("h-4 w-4", toneClasses[tone])} />}
      </div>
      <p className={cn("mt-2 font-heading text-3xl font-semibold", toneClasses[tone])}>{value}</p>
    </div>
  );
}

export function SectionTitle({ children, action }) {
  return (
    <div className="flex items-center justify-between mb-3 mt-1">
      <h2 className="font-heading text-lg font-semibold text-patria-ink">{children}</h2>
      {action}
    </div>
  );
}

export function EmptyState({ title, hint, testId }) {
  return (
    <div className="rounded-2xl border border-dashed border-patria-muted/30 bg-patria-surface/50 p-8 text-center" data-testid={testId}>
      <p className="font-medium text-patria-ink">{title}</p>
      {hint && <p className="text-sm text-patria-muted mt-1">{hint}</p>}
    </div>
  );
}
